import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../utils/api';
import {
  Typography,
  Card,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Space,
  Popconfirm,
  message,
  Switch,
  Select,
  Alert,
} from 'antd';
import { PlusOutlined, EyeOutlined } from '@ant-design/icons';
import {
  canMutateSharedInfra,
  SHARED_INFRA_READONLY_MESSAGE,
} from '../../utils/permissions';

const { Title, Text, Paragraph } = Typography;

interface AuditRule {
  id: number;
  category: string;
  matchType: string;
  matchValue: string;
  action: string;
  method: string;
  network: string | null;
  appliesToProfile: string;
  isEnabled: boolean;
  sortOrder: number;
  description: string | null;
}

const CATEGORY_LABEL: Record<string, string> = {
  bt: 'BitTorrent',
  smtp: 'SMTP/垃圾郵件',
  private: '私有段/SSRF',
  political: '政治',
  cn_sdk: '国产SDK',
  malware: '惡意/C2',
  csam: '兒少 CSAM',
};
const MATCH_TYPE_LABEL: Record<string, string> = {
  domain_suffix: '域名後綴',
  domain_keyword: '域名關鍵字',
  protocol: '協議',
  ip_cidr: 'IP 段',
  port: '埠',
};
const PROFILE_LABEL: Record<string, string> = {
  all: '全部節點',
  egress: '僅海外',
  cn_return: '僅回國',
};
const CATEGORY_OPTIONS = Object.keys(CATEGORY_LABEL).map(v => ({
  value: v,
  label: CATEGORY_LABEL[v],
}));
const MATCH_TYPE_OPTIONS = Object.keys(MATCH_TYPE_LABEL).map(v => ({
  value: v,
  label: MATCH_TYPE_LABEL[v],
}));
const PROFILE_OPTIONS = Object.keys(PROFILE_LABEL).map(v => ({
  value: v,
  label: PROFILE_LABEL[v],
}));

export const AuditRulesPage: React.FC = () => {
  const sharedInfraReadOnly = !canMutateSharedInfra();
  const [rules, setRules] = useState<AuditRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string | undefined>();
  const [filterProfile, setFilterProfile] = useState<string | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [form] = Form.useForm();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filterCategory) qs.set('category', filterCategory);
      if (filterProfile) qs.set('profile', filterProfile);
      const res = await authFetch(`/api/admin/audit-rules?${qs.toString()}`);
      const data = await res.json();
      if (data.code === 0) setRules(data.data);
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterProfile]);

  useEffect(() => {
    fetch_();
  }, [fetch_]);

  const guard = (): boolean => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return false;
    }
    return true;
  };

  const openAdd = () => {
    if (!guard()) return;
    setEditingId(null);
    form.resetFields();
    setModalOpen(true);
  };
  const openEdit = (r: AuditRule) => {
    if (!guard()) return;
    setEditingId(r.id);
    form.setFieldsValue(r);
    setModalOpen(true);
  };

  const handleOk = async () => {
    if (!guard()) return;
    try {
      const values = await form.validateFields();
      setModalLoading(true);
      const url = editingId
        ? `/api/admin/audit-rules/${editingId}`
        : '/api/admin/audit-rules';
      const method = editingId ? 'PUT' : 'POST';
      const res = await authFetch(url, {
        method,
        body: JSON.stringify(values),
      });
      const data = await res.json();
      if (data.code === 0) {
        message.success(data.msg || (editingId ? '更新成功' : '新增成功'));
        setModalOpen(false);
        fetch_();
      } else {
        message.error(data.msg || '操作失敗');
      }
    } catch {
      /* validation error */
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!guard()) return;
    const res = await authFetch(`/api/admin/audit-rules/${id}`, {
      method: 'DELETE',
    });
    const data = await res.json();
    if (data.code === 0) {
      message.success(data.msg || '已刪除');
      fetch_();
    } else {
      message.error(data.msg || '刪除失敗');
    }
  };

  const handleToggle = async (r: AuditRule, enabled: boolean) => {
    if (!guard()) return;
    const res = await authFetch(`/api/admin/audit-rules/${r.id}/toggle`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    });
    const data = await res.json();
    if (data.code === 0) {
      fetch_();
    } else {
      message.error(data.msg || '切換失敗');
    }
  };

  const handleToggleCategory = async (category: string, enabled: boolean) => {
    if (!guard()) return;
    const res = await authFetch('/api/admin/audit-rules/toggle-category', {
      method: 'POST',
      body: JSON.stringify({ category, enabled }),
    });
    const data = await res.json();
    if (data.code === 0) {
      message.success(data.msg || '已切換');
      fetch_();
    } else {
      message.error(data.msg || '切換失敗');
    }
  };

  const handlePreview = async (profile: string) => {
    const res = await authFetch(
      `/api/admin/audit-rules/preview?profile=${profile}`
    );
    const data = await res.json();
    if (data.code === 0) {
      setPreviewData(data.data);
      setPreviewOpen(true);
    } else {
      message.error(data.msg || '預覽失敗');
    }
  };

  const columns = [
    {
      title: '分類',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (v: string) => <Tag>{CATEGORY_LABEL[v] || v}</Tag>,
    },
    {
      title: '比對類型',
      dataIndex: 'matchType',
      key: 'matchType',
      width: 110,
      render: (v: string) => MATCH_TYPE_LABEL[v] || v,
    },
    {
      title: '比對值',
      dataIndex: 'matchValue',
      key: 'matchValue',
      ellipsis: true,
      render: (v: string) => (
        <Text style={{ fontSize: 12 }} code>
          {v}
        </Text>
      ),
    },
    {
      title: '套用',
      dataIndex: 'appliesToProfile',
      key: 'appliesToProfile',
      width: 100,
      render: (v: string) => {
        const color =
          v === 'all' ? 'blue' : v === 'egress' ? 'geekblue' : 'gold';
        return <Tag color={color}>{PROFILE_LABEL[v] || v}</Tag>;
      },
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 60,
    },
    {
      title: '啟用',
      key: 'enabled',
      width: 70,
      render: (_: unknown, r: AuditRule) => (
        <Switch
          size="small"
          checked={r.isEnabled}
          disabled={sharedInfraReadOnly}
          onChange={checked => handleToggle(r, checked)}
        />
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 140,
      render: (_: unknown, r: AuditRule) => (
        <Space size="small">
          <Button
            size="small"
            disabled={sharedInfraReadOnly}
            onClick={() => openEdit(r)}
          >
            編輯
          </Button>
          <Popconfirm title="確定刪除？" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger disabled={sharedInfraReadOnly}>
              刪除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Title level={3}>節點封鎖規則（Audit Rule）</Title>
      <Paragraph type="secondary">
        V2bX 式動態封鎖規則：增刪改後存 DB，節點 syncer 5 分鐘內自動拉取套用，
        零部署。海外節點 = 用戶翻出去；回國節點 = 海外用戶回中國（国产SDK
        類規則設「僅海外」避免傷回國可達）。
      </Paragraph>

      <Card
        size="small"
        style={{ marginBottom: 12 }}
        title="分類批次開關"
        styles={{ body: { display: 'flex', gap: 16, flexWrap: 'wrap' } }}
      >
        {Object.keys(CATEGORY_LABEL).map(cat => (
          <Space key={cat} size={4}>
            <Text>{CATEGORY_LABEL[cat]}</Text>
            <Button
              size="small"
              disabled={sharedInfraReadOnly}
              onClick={() => handleToggleCategory(cat, true)}
            >
              全開
            </Button>
            <Button
              size="small"
              disabled={sharedInfraReadOnly}
              onClick={() => handleToggleCategory(cat, false)}
            >
              全關
            </Button>
          </Space>
        ))}
      </Card>

      <Card
        title="規則列表"
        extra={
          <Space wrap>
            <Select
              allowClear
              placeholder="分類篩選"
              style={{ width: 140 }}
              options={CATEGORY_OPTIONS}
              value={filterCategory}
              onChange={setFilterCategory}
            />
            <Select
              allowClear
              placeholder="節點類型篩選"
              style={{ width: 140 }}
              options={PROFILE_OPTIONS}
              value={filterProfile}
              onChange={setFilterProfile}
            />
            <Button
              icon={<EyeOutlined />}
              onClick={() => handlePreview('egress')}
            >
              預覽海外
            </Button>
            <Button
              icon={<EyeOutlined />}
              onClick={() => handlePreview('cn_return')}
            >
              預覽回國
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={sharedInfraReadOnly}
              onClick={openAdd}
            >
              新增規則
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={rules}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 900 }}
          size="small"
        />
      </Card>

      <Modal
        title={editingId ? '編輯規則' : '新增規則'}
        open={modalOpen}
        onOk={handleOk}
        onCancel={() => setModalOpen(false)}
        confirmLoading={modalLoading}
        destroyOnClose
        width={560}
      >
        <Alert
          type="info"
          message="比對值多筆用逗號或換行分隔。domain_keyword 比對 SNI/host 子字串；ip_cidr/port 不需 sniff；protocol=bittorrent 會自動加 BT sniffer。"
          style={{ marginBottom: 12 }}
        />
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            action: 'reject',
            method: 'default',
            appliesToProfile: 'all',
            sortOrder: 0,
            isEnabled: true,
          }}
        >
          <Form.Item
            name="category"
            label="分類"
            rules={[{ required: true }]}
          >
            <Select options={CATEGORY_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="matchType"
            label="比對類型"
            rules={[{ required: true }]}
          >
            <Select options={MATCH_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="matchValue"
            label="比對值（逗號/換行分隔）"
            rules={[{ required: true }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="如 25,465,587 或 falundafa,minghui 或 169.254.169.254/32"
            />
          </Form.Item>
          <Form.Item name="network" label="網路（僅 port 規則用）">
            <Select
              allowClear
              options={[
                { value: 'tcp', label: 'tcp' },
                { value: 'udp', label: 'udp' },
              ]}
              placeholder="port 規則預設 tcp"
            />
          </Form.Item>
          <Form.Item
            name="appliesToProfile"
            label="套用節點類型"
            rules={[{ required: true }]}
          >
            <Select options={PROFILE_OPTIONS} />
          </Form.Item>
          <Form.Item name="method" label="reject 方式">
            <Select
              options={[
                { value: 'default', label: 'default（回 RST/ICMP）' },
                { value: 'drop', label: 'drop（靜默丟棄）' },
              ]}
            />
          </Form.Item>
          <Form.Item name="sortOrder" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="說明">
            <Input placeholder="人類可讀說明（選填）" />
          </Form.Item>
          <Form.Item name="isEnabled" label="啟用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`預覽生成的 sing-box route rule（${previewData ? PROFILE_LABEL[previewData.profile] : ''}）`}
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={640}
      >
        {previewData && (
          <>
            <Paragraph>
              <Text strong>套用規則數:</Text> {previewData.ruleCount}
              {previewData.extraSniffers?.length > 0 && (
                <>
                  {' '}
                  <Text strong>額外 sniffer:</Text>{' '}
                  {previewData.extraSniffers.join(', ')}
                </>
              )}
            </Paragraph>
            <pre
              style={{
                background: 'rgba(0,0,0,0.04)',
                padding: 12,
                borderRadius: 4,
                fontSize: 12,
                maxHeight: 400,
                overflow: 'auto',
              }}
            >
              {JSON.stringify(previewData.routeRules, null, 2)}
            </pre>
          </>
        )}
      </Modal>
    </div>
  );
};

export default AuditRulesPage;
